<template>
    <CqModal
        :showModal="show"
        @closeModal="closeModal"
        heading="Geschiedenis"
        maxWidth="calc(100vw - 4rem)"
    >
        <div :class="{'skeleton-load': isLoading}">
            <CqPlaceholderText :words="100" :lines="10" v-if="isLoading" />
            <div v-else-if="!hasDeploymentHistorySets">
                Er zijn nog geen acties uitgevoerd op deze dag.
            </div>
            <div class="deployment-history" v-else>
                <InfoGrid>
                    <InfoHeader>Werknemer</InfoHeader><span>{{ employeeName }}</span>
                    <InfoHeader>Datum</InfoHeader><span>{{ formattedDate }}</span>
                </InfoGrid>
                <div class="listContainer">
                    <CqList v-model="historyHeaders" sortable :columnDefinition="{'wide': '8rem 12rem 8rem 8rem minmax(18rem, 1fr) 10rem 12rem'}">
                        <HistoryEntrySet
                            v-for="historyEntrySet in sortedEntries"
                            :key="historyEntrySet.lastAction.createDate.getTime()"
                            :shifts="shifts"
                            :rosterNames="rosterNames"
                            :deploymentHistorySet="historyEntrySet"
                            :organisationDeploymentStates="organisationDeploymentStates"
                            :organisationDeploymentTypes="organisationDeploymentTypes"
                            :projects="projects"
                            :canRestore="canRestore(historyEntrySet)"
                            @restore:history="emitRestore(historyEntrySet)"
                        />
                    </CqList>
                </div>
            </div>
        </div>
        <CqButtonBar>
            <CqButton @click="closeModal" label="Annuleren" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {CqButton, CqButtonBar, CqList, CqModal, CqPlaceholderText, CqListUtil, CqListHeaderDefinition} from '@cquential/ui';
    import HistoryEntrySet from '@/components/modals/DeploymentHistoryModal/HistoryEntrySet.vue';
    import {ApiClient, DeploymentHistory, OrganisationDeploymentState, DeploymentType, Shift, Project, Deployment, DeploymentStateNumber} from '@cquential/api';
    import {DeploymentHistorySet, DeploymentHistorySetEntry} from '@/components/modals/types';
    import {ChangedDeploymentSet} from '@/types/emits';
    import Dates from '@cquential/date-utils';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';
    import {ResourceWithKeyedByDateObjects, CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments} from '@/types/api-object-extensions';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqList,
            CqModal,
            CqPlaceholderText,
            HistoryEntrySet,
            InfoGrid,
            InfoHeader,
        },
        emits: [
            'restore:deployments',
        ],
    })
    export default class DeploymentHistoryModal extends Vue {
        private static instance: DeploymentHistoryModal | null = null;

        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({required: true}) public organisationDeploymentTypes!: Record<number, DeploymentType>;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({required: true}) public defaultShiftIdPerRoster!: Record<number, number>;
        @Prop({required: true}) public shiftIdsOnCurrentRoster!: number[];
        @Prop() public projects?: Project[];

        private show: boolean = false;
        private date: Date | null = null;
        private resource: ResourceWithKeyedByDateObjects | null = null;
        private company: CompanyWithDeploymentsAndEmployees | null = null;
        private companyEmployee: CompanyEmployeeWithDeployments | null = null;
        private canEdit: boolean = false;
        private deploymentHistorySets: Record<number, DeploymentHistorySet> = {};
        private deploymentIdsGroupedByCurrentlyActive: Record<number, number[]> = {};
        private isLoading: boolean = false;

        private historyHeaders: CqListHeaderDefinition<DeploymentHistorySet, 'date' | 'editor' | 'roster' | 'shift'>[] = [
            {
                label: 'Datum',
                identifier: 'date',
                currentSort: 'DESC',
                comparer: (a: DeploymentHistorySet, b: DeploymentHistorySet): number => CqListUtil.genericCompare(a.lastAction.createDate, b.lastAction.createDate),
            },
            {
                label: 'Door',
                identifier: 'editor',
                comparer: (a: DeploymentHistorySet, b: DeploymentHistorySet): number => {
                    let compareResult: number = CqListUtil.genericCompare(a.lastAction.editedByFullname, b.lastAction.editedByFullname);
                    if (compareResult === 0) {
                        compareResult = CqListUtil.genericCompare(a.lastAction.editedBy, b.lastAction.editedBy);
                    }

                    return compareResult;
                },
            },
            {
                label: 'Rooster',
                identifier: 'roster',
                comparer: (a: DeploymentHistorySet, b: DeploymentHistorySet): number => {
                    let firstRosterName: string | null = null;
                    let secondRosterName: string | null = null;
                    // use roster of first entry
                    for (const n in a.entries) {
                        firstRosterName = this.rosterNames[this.shifts[a.entries[n].deployment.shiftId].rosterId];
                        break;
                    }
                    for (const n in b.entries) {
                        secondRosterName = this.rosterNames[this.shifts[b.entries[n].deployment.shiftId].rosterId];
                        break;
                    }

                    return CqListUtil.genericCompare(firstRosterName, secondRosterName);
                },
            },
            {
                label: 'Dienst',
                identifier: 'shift',
                comparer: (a: DeploymentHistorySet, b: DeploymentHistorySet): number => {
                    let firstShiftLabel: string | null = null;
                    let secondShiftLabel: string | null = null;
                    // use shift of first entry
                    for (const n in a.entries) {
                        firstShiftLabel = this.shifts[a.entries[n].deployment.shiftId].template!.code;
                        break;
                    }
                    for (const n in b.entries) {
                        secondShiftLabel = this.shifts[b.entries[n].deployment.shiftId].template!.code;
                        break;
                    }

                    return CqListUtil.genericCompare(firstShiftLabel, secondShiftLabel);
                },
            },
            {
                label: 'Specificatie',
                unsortable: true,
            },
            {
                label: 'Bewerking op gepubliceerd rooster',
                unsortable: true,
            },
            {
                label: 'Acties',
                unsortable: true,
            },
        ];

        public mounted(): void {
            DeploymentHistoryModal.instance = this;
        }

        public beforeUnmount(): void {
            DeploymentHistoryModal.instance = null;
        }

        public get employeeName(): string {
            return ResourceUtil.displayName(this.resource) ?? this.companyEmployee?.name ?? this.company?.name ?? '';
        }

        public get formattedDate(): string {
            if (!this.date) {
                return '';
            }

            return Dates.Formatter.formatDateLong(this.date);
        }

        public get lastEntryTime(): number {
            return Math.max(...Object.keys(this.deploymentHistorySets).map(Number));
        }

        public get sortedEntries(): DeploymentHistorySet[] {
            return CqListUtil.sortData(Object.values(this.deploymentHistorySets), this.historyHeaders);
        }

        public get currentDeploymentHistorySet(): DeploymentHistorySet {
            return this.deploymentHistorySets[this.lastEntryTime];
        }

        public get hasDeploymentHistorySets(): boolean {
            for (const _ in this.deploymentHistorySets) {
                return true;
            }

            return false;
        }

        public closeModal(): void {
            this.show = false;
            this.date = null;
            this.resource = null;
            this.company = null;
            this.companyEmployee = null;
            this.canEdit = false;
            this.deploymentHistorySets = {};
        }

        public loadDeploymentHistory(): void {
            if (!this.date) {
                return;
            }

            this.isLoading = true;
            ApiClient.instance.fetchDeploymentsChangedOn(this.date, this.resource?.id ?? undefined, this.company?.id ?? undefined, [
                'oldDeployment.specifications',
                'oldDeployment.positionAssignmentSetGroupPositionDeployments',
                'newDeployment.specifications',
                'newDeployment.positionAssignmentSetGroupPositionDeployments',
            ]).then((historyEntries: DeploymentHistory[]): void => {
                const currentSet: Pick<DeploymentHistorySet, 'entries'> = {entries: {}};
                const loadedDeploymentHistorySets: Record<number, DeploymentHistorySet> = {};
                this.deploymentIdsGroupedByCurrentlyActive = {};

                if (this.company?.id) {
                    historyEntries = historyEntries.filter((historyEntry: DeploymentHistory): boolean => {
                        const shiftId: number | undefined = historyEntry.newDeployment?.shiftId ?? historyEntry.oldDeployment?.shiftId;
                        if (!shiftId) {
                            return false;
                        }

                        return this.shiftIdsOnCurrentRoster.includes(shiftId);
                    });
                }

                for (const historyEntry of historyEntries) {
                    if (historyEntry.oldDeplId in currentSet.entries) {
                        delete currentSet.entries[historyEntry.oldDeplId];
                    }
                    currentSet.entries[historyEntry.newDeplId] = {
                        changeSource: historyEntry.changeSource,
                        editOnPublishedRoster: historyEntry.editOnPublishedRoster,
                        deployment: historyEntry.newDeplId > 0 ? historyEntry.newDeployment! : this.getEmptyDeploymentWithDefaultShift(historyEntry),
                    };

                    if (historyEntry.newDeplId > 0) {
                        this.deploymentIdsGroupedByCurrentlyActive[historyEntry.newDeplId] = [
                            ...(this.deploymentIdsGroupedByCurrentlyActive[historyEntry.oldDeplId] ?? []),
                            historyEntry.newDeplId,
                        ];
                    }
                    delete this.deploymentIdsGroupedByCurrentlyActive[historyEntry.oldDeplId];

                    if (typeof currentSet.entries[0] !== 'undefined' && Object.keys(currentSet.entries).length > 1) {
                        delete currentSet.entries[0];
                    }

                    loadedDeploymentHistorySets[historyEntry.createDate.getTime()] = {lastAction: historyEntry, entries: {...currentSet.entries}};
                }

                this.deploymentHistorySets = loadedDeploymentHistorySets;
            }).finally((): void => {
                this.isLoading = false;
            });
        }

        public canRestore(historyEntrySet: DeploymentHistorySet): boolean {
            if (this.company) {
                return false;
            }

            if (!this.date) {
                return false;
            }

            if (historyEntrySet.lastAction.createDate.getTime() === this.lastEntryTime) {
                return false;
            }

            if (this.resource && this.resource.salaryAcceptedUntil > this.date) {
                return false;
            }

            return this.canEdit;
        }

        public emitRestore(historySet: DeploymentHistorySet): void {
            const changedDeploymentSet: ChangedDeploymentSet = {
                save: [],
                remove: [],
                resource: this.resource ?? undefined,
                company: this.company ?? undefined,
                companyEmployee: this.companyEmployee ?? undefined,
            };
            const usedDeploymentIds: number[] = [];

            for (const deploymentId in historySet.entries) {
                if (deploymentId === '0') {
                    continue;
                }
                let currentDeploymentId: number = 0;
                for (const activeDeploymentId in this.deploymentIdsGroupedByCurrentlyActive) {
                    if (this.deploymentIdsGroupedByCurrentlyActive[activeDeploymentId]?.includes(Number(deploymentId))) {
                        currentDeploymentId = Number(activeDeploymentId);
                        usedDeploymentIds.push(currentDeploymentId);
                        break;
                    }
                }

                const newDeployment: Deployment = {
                    ...historySet.entries[Number(deploymentId)].deployment,
                };

                if (currentDeploymentId === 0) {
                    delete newDeployment.id;
                } else {
                    newDeployment.id = currentDeploymentId;
                }

                changedDeploymentSet.save.push(newDeployment);
            }

            const currentActiveDeploymentIds: number[] = Object.keys(this.deploymentIdsGroupedByCurrentlyActive).map((deploymentId: string): number => Number(deploymentId));
            const deploymentsWithUnassignedId: Deployment[] = changedDeploymentSet.save.filter((deployment: Deployment): boolean => !('id' in deployment) || !deployment.id);
            const unusedActiveDeploymentIds: number[] = currentActiveDeploymentIds.filter((deploymentId: number): boolean => !usedDeploymentIds.includes(deploymentId));

            // Assign active deployment ids to deployments without related id
            if (deploymentsWithUnassignedId.length > 0 && unusedActiveDeploymentIds.length > 0) {
                for (const deployment of deploymentsWithUnassignedId) {
                    deployment.id = unusedActiveDeploymentIds[0];
                    unusedActiveDeploymentIds.splice(0, 1);

                    if (unusedActiveDeploymentIds.length === 0) {
                        break;
                    }
                }
            }

            // Delete all deployments that are still active
            if (unusedActiveDeploymentIds.length > 0) {
                changedDeploymentSet.remove = Object.values(this.currentDeploymentHistorySet.entries)
                    .filter((history: DeploymentHistorySetEntry): boolean => {
                        return unusedActiveDeploymentIds.includes(history.deployment.id!);
                    })
                    .map((history: DeploymentHistorySetEntry): Deployment => {
                        return history.deployment;
                    });
            }

            this.$emit('restore:deployments', changedDeploymentSet);
            this.closeModal();
        }

        public getEmptyDeploymentWithDefaultShift(historyEntry: DeploymentHistory): Deployment {
            const oldRosterId: number = this.shifts[historyEntry.oldDeployment!.shiftId].rosterId;

            return {
                publicComment: null,
                shiftId: this.shifts[this.defaultShiftIdPerRoster[oldRosterId]].id!,
                date: historyEntry.deplDate,
                stateId: DeploymentStateNumber.Normal,
                resourceId: historyEntry.resourceId,
                companyId: historyEntry.otherCompanyId,
                companyEmployeeId: null,
                companyEmployee: undefined,
                specified: false,
            };
        }

        public static openModal(
            date: Date,
            resource: ResourceWithKeyedByDateObjects | null,
            company: CompanyWithDeploymentsAndEmployees | null,
            companyEmployee: CompanyEmployeeWithDeployments | null,
            canEdit: boolean = false,
        ): void {
            this.instance!.show = true;
            this.instance!.date = date;
            this.instance!.resource = resource;
            this.instance!.company = company;
            this.instance!.companyEmployee = companyEmployee;
            this.instance!.canEdit = canEdit;

            this.instance!.loadDeploymentHistory();
        }
    }
</script>

<style lang="scss" scoped>
    .listContainer {
        overflow: auto;
    }
</style>
